// Pagina attivazione account — impostazione password da link invito
import { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Lock, Eye, EyeOff, CheckCircle2, Loader2 } from 'lucide-react'
import { Input } from '../../components/ui/Input'
import { Button } from '../../components/ui/Button'
import { Alert } from '../../components/ui/Alert'
import { useAuthStore } from '../../stores/auth.store'
import type { User } from '@fieldcore/types'
import api from '../../lib/api'

const schema = z.object({
  password: z.string().min(8, 'Minimo 8 caratteri'),
  confirm: z.string(),
}).refine((d) => d.password === d.confirm, {
  message: 'Le password non coincidono',
  path: ['confirm'],
})

type FormData = z.infer<typeof schema>

interface UserInfo {
  email: string
  firstName: string
  lastName: string
}

export function SetPasswordPage() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const setAuth = useAuthStore((s) => s.setAuth)
  const token = params.get('token') ?? ''

  const [userInfo, setUserInfo] = useState<UserInfo | null>(null)
  const [tokenError, setTokenError] = useState<string | null>(null)
  const [showPass, setShowPass] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [done, setDone] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<FormData>({
    resolver: zodResolver(schema),
  })

  // Verifica token all'apertura
  useEffect(() => {
    if (!token) {
      setTokenError('Link non valido. Chiedi un nuovo invito.')
      return
    }
    api.get<UserInfo>(`/auth/magic-link/verify/${token}`)
      .then((r) => setUserInfo(r.data))
      .catch((e) => {
        const msg = e?.response?.data?.message ?? 'Link non valido o scaduto.'
        setTokenError(msg)
      })
  }, [token])

  const onSubmit = async (data: FormData) => {
    setSubmitError(null)
    try {
      const res = await api.post<{
        accessToken: string
        refreshToken: string
        user: { id: string; email: string; firstName: string; lastName: string; role: string }
      }>('/auth/magic-link/set-password', { token, password: data.password })

      // Salva sessione e vai alla dashboard
      setAuth(
        { accessToken: res.data.accessToken, refreshToken: res.data.refreshToken },
        res.data.user as unknown as User,
      )
      setDone(true)
      setTimeout(() => navigate('/'), 1500)
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? 'Errore durante il salvataggio. Riprova.'
      setSubmitError(msg)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <span className="text-2xl font-bold tracking-tight text-gray-900">KleisNote</span>
          <p className="mt-1 text-sm text-gray-500">Attivazione account</p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
          {tokenError ? (
            <div className="text-center space-y-4">
              <Alert variant="error">{tokenError}</Alert>
              <button
                onClick={() => navigate('/login')}
                className="text-sm text-blue-600 hover:underline"
              >
                Torna al login
              </button>
            </div>
          ) : !userInfo ? (
            <div className="flex flex-col items-center gap-3 py-6">
              <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
              <p className="text-sm text-gray-500">Verifica link in corso...</p>
            </div>
          ) : done ? (
            <div className="flex flex-col items-center gap-3 py-6 text-center">
              <CheckCircle2 className="h-10 w-10 text-green-500" />
              <p className="font-semibold text-gray-900">Account attivato!</p>
              <p className="text-sm text-gray-500">Accesso in corso...</p>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <p className="text-base font-semibold text-gray-900">
                  Benvenuto, {userInfo.firstName}!
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  Scegli una password per <span className="font-medium text-gray-700">{userInfo.email}</span>
                </p>
              </div>

              {submitError && <Alert variant="error" className="mb-4">{submitError}</Alert>}

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div className="relative">
                  <Input
                    label="Password"
                    type={showPass ? 'text' : 'password'}
                    leftIcon={<Lock className="h-4 w-4" />}
                    error={errors.password?.message}
                    autoFocus
                    {...register('password')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass((v) => !v)}
                    className="absolute right-3 top-8 text-gray-400 hover:text-gray-600"
                    tabIndex={-1}
                  >
                    {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>

                <div className="relative">
                  <Input
                    label="Conferma password"
                    type={showConfirm ? 'text' : 'password'}
                    leftIcon={<Lock className="h-4 w-4" />}
                    error={errors.confirm?.message}
                    {...register('confirm')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm((v) => !v)}
                    className="absolute right-3 top-8 text-gray-400 hover:text-gray-600"
                    tabIndex={-1}
                  >
                    {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>

                <p className="text-xs text-gray-400">Minimo 8 caratteri.</p>

                <Button type="submit" className="w-full" isLoading={isSubmitting}>
                  Imposta password e accedi
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
